import React from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";

/**
 * attended — the patient turned up. The tick, because it is the one a glance at the calendar is
 *   really looking for: a missed session already shows a red frame, and until now an attended one
 *   looked exactly like a session nobody had touched.
 * saved   — the appointment has an action report saved against it. This used to own the tick.
 * report  — the therapist has filled in their report.
 */
export type BadgeVariant = "attended" | "saved" | "report";

interface ActionReportBadgeProps {
  variant: BadgeVariant;
  size?: number;
  /** Override tooltip (e.g. for the legend) */
  title?: string;
}

const wrapperStyle = {
  display: "inline-flex" as const,
  alignItems: "center" as const,
  flexShrink: 0,
};

const DEFAULT_TITLE_KEYS: Record<BadgeVariant, [string, string]> = {
  attended: ["ATTENDANCE_ATTENDED", "הגיע"],
  saved: ["APPOINTMENTS_ICON_LEGEND_SAVE", "שמירת פגישה"],
  report: ["APPOINTMENTS_ICON_LEGEND_REPORT", "קיים דוח"],
};

/** Icon-only badges shown on an appointment, in the style of a Google Calendar event emoji. */
const ActionReportBadge: React.FC<ActionReportBadgeProps> = ({
  variant,
  size = 14,
  title: titleOverride,
}) => {
  const [key, fallback] = DEFAULT_TITLE_KEYS[variant];
  const title = titleOverride ?? (t(key) || fallback);

  return (
    <span title={title} style={{ ...wrapperStyle, color: "#10B981" }}>
      {variant === "attended" && (
        <span style={{ fontSize: `${size}px`, fontWeight: "bold", lineHeight: 1 }}>
          ✓
        </span>
      )}

      {/* A floppy disk — the save glyph every office application still uses. */}
      {variant === "saved" && (
        <svg
          viewBox="0 0 16 16"
          width={size}
          height={size}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M2 2a1 1 0 0 1 1-1h8.086a1 1 0 0 1 .707.293l2.914 2.914a1 1 0 0 1 .293.707V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2zm2 0v4h7V2H4zm5 1h1.5v2H9V3zM4 9h8v4H4V9z" />
        </svg>
      )}

      {variant === "report" && (
        <svg
          viewBox="0 0 16 16"
          width={size}
          height={size}
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M4 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5.5L9.5 1H4zm1 0v3.5h4V1H5zm0 5h6v1H5V6zm0 2h6v1H5V8zm0 2h4v1H5v-1z" />
        </svg>
      )}
    </span>
  );
};

export default ActionReportBadge;
